/* eslint-disable */
// Event-driven notification service using AppSync subscriptions
import { generateClient } from 'aws-amplify/api';

// Lazy client initialization to ensure Amplify is configured first
let client = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

// Subscription for real-time notifications
const NOTIFICATION_SUBSCRIPTION = `
  subscription OnNotificationCreated($userID: ID!) {
    onNotificationCreated(userID: $userID) {
      id
      userID
      type
      message
      metadata
      isRead
      createdAt
      updatedAt
    }
  }
`;

class EventNotificationService {
  constructor() {
    this.subscriptions = new Map();
    this.listeners = new Set();
  }

  // Subscribe to user notifications
  subscribe(userID, callback) {
    if (this.subscriptions.has(userID)) {
      this.subscriptions.get(userID).unsubscribe();
    }

    try {
      const subscription = getClient().graphql({
        query: NOTIFICATION_SUBSCRIPTION,
        variables: { userID }
      }).subscribe({
        next: ({ data }) => {
          const notification = data.onNotificationCreated;
          if (notification) {
            this.notifyListeners(notification);
            callback(notification);
          }
        },
        error: (error) => {
          console.error('Notification subscription error:', error);
          this.fallbackToPolling(userID, callback);
        }
      });

      this.subscriptions.set(userID, subscription);
      return subscription;
    } catch (error) {
      console.error('Failed to create subscription:', error);
      this.fallbackToPolling(userID, callback);
      return null;
    }
  }

  // Unsubscribe from notifications
  unsubscribe(userID) {
    const subscription = this.subscriptions.get(userID);
    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(userID);
    }
  }

  // Add notification listener
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // Notify all listeners
  notifyListeners(notification) {
    this.listeners.forEach(callback => {
      try {
        callback(notification);
      } catch (error) {
        console.error('Listener error:', error);
      }
    });
  }

  // Fallback to polling if subscription fails
  fallbackToPolling(userID, callback) {
    let lastNotificationTime = new Date().toISOString();
    
    const interval = setInterval(async () => {
      try {
        const response = await getClient().graphql({
          query: `
            query GetNotifications($userID: ID!, $limit: Int, $createdAt: ModelStringKeyConditionInput) {
              notificationsByUserIDAndCreatedAt(
                userID: $userID, 
                limit: $limit, 
                sortDirection: DESC,
                createdAt: $createdAt
              ) {
                items {
                  id
                  userID
                  type
                  message
                  metadata
                  isRead
                  createdAt
                  updatedAt
                }
              }
            }
          `,
          variables: { 
            userID, 
            limit: 10,
            createdAt: { gt: lastNotificationTime }
          }
        });
        
        const notifications = response.data?.notificationsByUserIDAndCreatedAt?.items || [];
        
        if (notifications.length > 0) {
          lastNotificationTime = notifications[0].createdAt;
          notifications.reverse().forEach(notification => {
            callback(notification);
          });
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 30000);
    
    this.pollingInterval = interval;
    
    return () => {
      clearInterval(interval);
    };
  }

  // Cleanup all subscriptions
  cleanup() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions.clear();
    this.listeners.clear();
  }
}

export const eventNotificationService = new EventNotificationService();